package top.wcpe.allincore.ui.runtime.ir.request

import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import kotlin.jvm.JvmStatic

/**
 * Request 执行器（C8 拆分自 ExprEvaluator）。
 *
 * 负责把 [StmtNode.Request] 编译产物落地为运行期行为：
 * - 求值 payload 表达式形成 Map
 * - 拆解四种回调（PENDING / SUCCESS / FAIL / TIMEOUT）
 * - 解析 timeoutMs（含 NaN / 无穷 / 负值 / Long 溢出校验，单位：毫秒）
 * - 注册 [PendingRequest] 并通过 [RequestDispatcher] 上报
 * - 同步执行 pending 体（net 形态注入 `{kind:"pending", payload:{}}`）
 */
object RequestExecutor {

    /** 默认请求超时（毫秒）；timeoutMs 表达式缺省时回退至此值。 */
    private const val DEFAULT_REQUEST_TIMEOUT_MS = 30_000L

    @JvmStatic
    fun runRequest(req: StmtNode.Request, ctx: EvalContext) {
        if (ctx.registry == null || ctx.requestDispatcher == null) {
            // 编译期应已排除非法状态：仅在解释器被裸跑（无 registry / dispatcher）时触发。
            throw DslRuntimeError("当前上下文不支持 request：缺少 RequestRegistry / RequestDispatcher")
        }
        val payload = evalPayload(req, ctx)
        val cbs = Callbacks.split(req.callbacks)
        // 旧 callback-body 形态：快照外层局部变量，回调（含 pending 同步路径与异步 deliver/timeout）
        // 执行时注入，让回调内能引用外层 var（与闭包 lambda 直觉一致）。
        // 状态机形态走 StmtNode.StateMachine 路径，不经过本方法，无需此处处理。
        val capturedVars = ctx.snapshotVars()
        // 顺序约束：register -> pending -> send。
        // 若 dispatcher 在 send() 内同步回调，register 必须先建立 PendingRequest。
        val requestId = registerPending(req, ctx, cbs, capturedVars)
        try {
            runPendingCallback(cbs.pending, req.isNetMode, ctx, capturedVars)
        } catch (pendingError: RuntimeException) {
            ctx.registry.take(requestId)
            throw pendingError
        }
        ctx.requestDispatcher.send(requestId, req.namespace, req.handler, payload)
    }

    /** 求值 payload 表达式列表，组装扁平 map（值可为 null：DSL 表达式可求值为 null）。 */
    private fun evalPayload(req: StmtNode.Request, ctx: EvalContext): LinkedHashMap<String, Any?> {
        val payload = LinkedHashMap<String, Any?>()
        for (entry in req.payload) {
            payload[entry.key] = ExprEvaluator.eval(entry.value, ctx)
        }
        return payload
    }

    /** 生成 requestId 并把 PendingRequest 注册进 registry。 */
    private fun registerPending(
        req: StmtNode.Request,
        ctx: EvalContext,
        cbs: Callbacks,
        capturedVars: Map<String, Any?>,
    ): String {
        // 调用方 execute 已在入口校验 registry 非空（缺失时直接返回），此处必非空。
        val registry = ctx.registry!!
        val requestId = registry.nextId()
        val timeoutMs = resolveTimeoutMs(req, ctx)
        val deadline = currentTimeMillis() + timeoutMs
        registry.register(
            PendingRequest(
                requestId,
                bodyOf(cbs.success), paramOf(cbs.success),
                bodyOf(cbs.fail), paramOf(cbs.fail),
                bodyOf(cbs.timeout), paramOf(cbs.timeout),
                req.isNetMode, deadline,
                capturedVars = capturedVars,
            ),
        )
        return requestId
    }

    /** 同步执行 pending 回调体；net 形态注入合成结果 {kind="pending", payload=空}。 */
    private fun runPendingCallback(
        pending: StmtNode.Request.Callback?,
        netMode: Boolean,
        ctx: EvalContext,
        capturedVars: Map<String, Any?>,
    ) {
        val body = bodyOf(pending)
        if (body.isEmpty()) return
        val param = paramOf(pending)
        if (netMode && param != null) {
            val locals = mapOf(param to buildNetResult("pending", null))
            ExprEvaluator.execute(body, ctx.withLocals(locals), capturedVars)
        } else {
            ExprEvaluator.execute(body, ctx, capturedVars)
        }
    }

    /** 构造 net.request 合成结果对象：{kind, payload}。供 [RequestCallbacks] 共用。 */
    @JvmStatic
    fun buildNetResult(kind: String, payload: Map<String, Any>?): LinkedHashMap<String, Any> {
        val r = LinkedHashMap<String, Any>()
        r["kind"] = kind
        r["payload"] = if (payload == null) emptyMap() else LinkedHashMap(payload)
        return r
    }

    /**
     * 求值 net.request 的 timeoutMs 表达式，单位为毫秒。
     *
     * 表达式缺省时回退到 [DEFAULT_REQUEST_TIMEOUT_MS]（30 秒）。
     * 非数值 / NaN / 无穷 / 负数 / 超出 `Long.MAX_VALUE - now` 一律抛 DslRuntimeError。
     */
    @Suppress("ThrowsCount")
    private fun resolveTimeoutMs(req: StmtNode.Request, ctx: EvalContext): Long {
        val timeoutExpr = req.timeoutMs
        if (timeoutExpr == null) {
            return DEFAULT_REQUEST_TIMEOUT_MS
        }
        val value = ExprEvaluator.eval(timeoutExpr, ctx)
        if (value !is Number) {
            throw DslRuntimeError("timeoutMs 必须为数值（单位：毫秒），实际: ${DslValueUtils.classOf(value)}")
        }
        val d = value.toDouble()
        if (d.isNaN() || d.isInfinite() || d < 0.0) {
            throw DslRuntimeError("timeoutMs 必须为非负有限数值（单位：毫秒），实际: $d")
        }
        // 以 "now + timeoutMs 不溢出 Long" 为上界，避免 deadline 翻负
        val maxAllowed = Long.MAX_VALUE - currentTimeMillis()
        if (d > maxAllowed.toDouble()) {
            throw DslRuntimeError("timeoutMs 过大将导致 deadline 溢出（上限约 $maxAllowed ms），实际: $d")
        }
        return d.toLong()
    }

    private fun bodyOf(cb: StmtNode.Request.Callback?): List<StmtNode> = cb?.body ?: emptyList()

    private fun paramOf(cb: StmtNode.Request.Callback?): String? = cb?.paramName

    /** 已拆解的四种回调。 */
    private class Callbacks(
        val pending: StmtNode.Request.Callback?,
        val success: StmtNode.Request.Callback?,
        val fail: StmtNode.Request.Callback?,
        val timeout: StmtNode.Request.Callback?,
    ) {
        companion object {
            fun split(all: List<StmtNode.Request.Callback>): Callbacks {
                var p: StmtNode.Request.Callback? = null
                var s: StmtNode.Request.Callback? = null
                var f: StmtNode.Request.Callback? = null
                var t: StmtNode.Request.Callback? = null
                for (cb in all) {
                    when (cb.kind) {
                        StmtNode.Request.Callback.Kind.PENDING -> p = cb
                        StmtNode.Request.Callback.Kind.SUCCESS -> s = cb
                        StmtNode.Request.Callback.Kind.FAIL -> f = cb
                        StmtNode.Request.Callback.Kind.TIMEOUT -> t = cb
                    }
                }
                return Callbacks(p, s, f, t)
            }
        }
    }
}
